import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ── Types ──────────────────────────────────────────────────────────────────

export type Regime = 'general' | 'pequeña_empresa' | 'microempresa';
export type EmploymentType = 'full_time' | 'part_time' | 'contract';
export type AttendanceStatus = 'present' | 'late' | 'absent' | 'vacation' | 'sick_leave' | 'permission';
export type PayrollStatus = 'draft' | 'processing' | 'approved' | 'paid';
export type BonusType = 'fixed' | 'percentage' | 'performance';
export type UserRole = 'admin' | 'hr' | 'employee';

export interface Company {
  id: string;
  name: string;
  ruc: string;
  address?: string;
  phone?: string;
  email?: string;
  regime: Regime;
  working_days_per_month: number;
  daily_work_hours: number;
  essalud_rate: number;
  snp_rate: number;
  afp_system: 'onp' | 'afp';
  assign_familiar: boolean;
  rmv: number;
  uit: number;   // Unidad Impositiva Tributaria vigente (ej: 5500 en 2026)
  created_at: string;
  updated_at: string;
}

export interface Profile {
  id: string;
  company_id: string;
  full_name?: string;
  role: UserRole;
}

export type PensionSystem = 'onp' | 'afp' | 'ninguno';
export type HealthSystem  = 'essalud' | 'sis';
export type ShiftType     = 'full' | 'morning' | 'afternoon';

export interface Employee {
  id: string;
  company_id: string;
  code?: string;
  first_name: string;
  last_name: string;
  dni: string;
  email?: string;
  phone?: string;
  position: string;
  department?: string;
  hire_date: string;
  base_salary: number;
  employment_type: EmploymentType;
  has_family_assignment: boolean;
  family_members: number;
  bank?: string;
  bank_account?: string;
  afp_code?: string;

  // ── Pensión y salud ─────────────────────────────────────────────────────
  pension_system?: PensionSystem;
  pension_rate?: number;
  health_system?: HealthSystem;

  // ── Horario individual ───────────────────────────────────────────────────
  // NULL = hereda el horario global de la empresa (company_settings)
  shift_type?: ShiftType;          // 'full' | 'morning' | 'afternoon'
  shift_start?: string;            // HH:MM  ej: '08:00'
  shift_end?: string;              // HH:MM  ej: '17:00'
  break_start?: string;            // HH:MM  ej: '13:00' (solo turno completo)
  break_end?: string;              // HH:MM  ej: '14:00' (solo turno completo)
  tolerance_minutes?: number;      // minutos de tolerancia individual

  active: boolean;
  created_at: string;
}

export interface Attendance {
  id: string;
  company_id: string;
  employee_id: string;
  date: string;
  check_in?: string;
  check_out?: string;
  expected_check_in?: string;
  expected_check_out?: string;
  late_minutes: number;
  overtime_minutes: number;
  hours_worked: number;
  status: AttendanceStatus;
  notes?: string;
}

export interface Bonus {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  type: BonusType;
  value: number;
  is_active: boolean;
  applies_to: string;
}

export interface Payroll {
  id: string;
  company_id: string;
  period_year: number;
  period_month: number;
  status: PayrollStatus;
  total_gross: number;
  total_deductions: number;
  total_net: number;
  total_essalud: number;
  processed_at?: string;
  notes?: string;
  created_at: string;
}

export interface PayrollItem {
  id: string;
  payroll_id: string;
  company_id: string;
  employee_id: string;
  base_salary: number;
  working_days: number;
  days_worked: number;
  overtime_hours: number;
  overtime_amount: number;
  family_assignment: number;
  gratification: number;
  bonuses_total: number;
  other_income: number;
  gross_salary: number;
  absence_deduction: number;
  tardiness_deduction: number;
  absent_days: number;
  late_minutes_total: number;
  onp_amount: number;
  afp_amount: number;
  income_tax: number;
  other_deductions: number;
  essalud_amount: number;
  cts_provision: number;
  total_deductions: number;
  net_salary: number;
  regime_applied?: string;
  calculation_notes?: any;
  employee?: Employee;
}

export interface CompanySettings {
  id: string;
  company_id: string;
  check_in_time: string;
  check_out_time: string;
  tolerance_minutes: number;
  overtime_rate: number;
  overtime_rate_sunday: number;
  currency: string;
  timezone: string;
  logo_url?: string;
}

export interface AttendanceJustification {
  id: string;
  company_id: string;
  employee_id: string;
  attendance_id?: string;
  date: string;
  type: 'absence' | 'tardiness' | 'vacation_request';
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by?: string;
  reviewed_at?: string;
  review_note?: string;
  created_at: string;
  // Campos exclusivos para vacation_request
  vacation_start_date?: string;
  vacation_end_date?: string;
  vacation_days_count?: number;
}

export interface VacationBalance {
  id: string;
  company_id: string;
  employee_id: string;
  year: number;
  days_entitled: number;
  days_taken: number;
  days_pending: number;
}
